import useSWR from 'swr'
import { WorkflowTemplateSummary } from '@digital-twins/shared-types'

import { workflowsApi } from '@/lib/api'

/**
 * List data for the Workflows lobby and for `WorkflowMenu`'s dropdown (Phase 2
 * of docs/plans/assistant-platform-v1-phase2-integration-map.md, section 1.2).
 *
 * `GET /workflows` sits behind `JwtAuthGuard` only, so a browsing org can list
 * templates here and still be refused a run by `PaidFirstPaymentGuard` on
 * `POST /workflow-runs` — `useStartWorkflowRun` surfaces that refusal, not
 * this hook.
 *
 * `isError` is distinct from an honestly empty list: a failed fetch must not
 * render the "Fairgen builds workflows for your team" empty state, which
 * would tell an org with a real outage that they simply have no workflows.
 */
export interface UseWorkflowTemplatesResult {
  templates: Array<WorkflowTemplateSummary>
  isLoading: boolean
  isError: boolean
  retry: () => void
}

export interface UseWorkflowTemplatesOptions {
  /**
   * Widens `GET /workflows` to include `surface: 'thread'` command templates.
   * Uses a separate SWR key so the lobby list stays the default hide.
   */
  includeThreadActions?: boolean
  /**
   * When false the SWR key is null and no request fires. Defaults to true so
   * existing call sites are unchanged. Home and Threads pass the
   * assistantPlatform flag (assistant-beta-launch-plan Step 3).
   */
  enabled?: boolean
}

export function useWorkflowTemplates(
  options: UseWorkflowTemplatesOptions = {},
): UseWorkflowTemplatesResult {
  const includeThreadActions = options.includeThreadActions === true
  const enabled = options.enabled !== false
  const { data, isLoading, error, mutate } = useSWR(
    enabled
      ? includeThreadActions
        ? ['workflow-templates', 'thread-actions']
        : 'workflow-templates'
      : null,
    () => workflowsApi.listTemplates(false, includeThreadActions),
  )

  return {
    templates: data ?? [],
    isLoading,
    isError: Boolean(error),
    retry: () => {
      void mutate()
    },
  }
}
